import { useCallback, useMemo } from "react";

import { ExecutorLocations } from "../../services";
import { ExecutorStatus } from "../../services/Executor";
import { useTypedSelector } from "../../redux/store";
import { useCarParkFilterAccess } from "../../access";
import { useCarParkIdsDecoder } from "..";

const useExecutorLocationsSubHandler =
	(): useExecutorLocationsSubHandler.Props => {
		const { isUserFleetOwner, accessFleetsIdsOnlyActive } =
			useCarParkFilterAccess<any>();
		const allCarParkIds = useCarParkIdsDecoder();

		const _executorLocationsSubscribe = useTypedSelector(
			(state) =>
				state.globalState.executorLocations.data.subscription.basic,
		);
		const cars = useTypedSelector((state) => state.orders.map.data.cars);

		const fleetIdsAll = useMemo(
			() => allCarParkIds(["all"]),
			[allCarParkIds],
		);

		const executorLocationsSubscribe = useMemo(() => {
			//* merge park to executorLocation
			if (!Array.isArray(_executorLocationsSubscribe)) {
				return [];
			}

			if (!Array.isArray(cars)) {
				return _executorLocationsSubscribe;
			}

			const retval =
				_executorLocationsSubscribe
					?.map((item) => {
						const { carId } = item;
						const park = cars.find((car) => car.id === carId)?.park;
						const mergeParkWithExecutorLocalions: ExecutorLocations.Model =
							{
								...item,
								park,
							};

						return mergeParkWithExecutorLocalions;
					})
					// * Access to carPark for user
					?.filter((item) =>
						isUserFleetOwner
							? (accessFleetsIdsOnlyActive || []).includes(
									item.park?.id ?? -2,
							  )
							: true,
					) || [];

			return retval;
		}, [
			_executorLocationsSubscribe,
			accessFleetsIdsOnlyActive,
			cars,
			isUserFleetOwner,
		]);

		const getAllExecutorLocationsSubHandler = useCallback(
			({
				limit,
				taxiServiceIds,
				orderIds,
				executorWorkingStatus,
				executorStatuses,
				fleetIds,
			}: useExecutorLocationsSubHandler.SmallSubscribeOptions): ExecutorLocations.Model[] => {
				if (!Array.isArray(executorLocationsSubscribe)) {
					return [];
				}

				let retval = [...(executorLocationsSubscribe || [])];

				const [
					isTaxiServiceIds,
					isLimit,
					isOrderIds,
					isExecutorWorkingStatus,
					isExecutorStatuses,
					isFleetIds,
				] = [
					Array.isArray(taxiServiceIds) && taxiServiceIds?.length,
					typeof limit === "number",
					Array.isArray(orderIds) && orderIds?.length,
					typeof executorWorkingStatus === "boolean",
					Array.isArray(executorStatuses) && executorStatuses?.length,
					Array.isArray(fleetIds) &&
						fleetIds?.length &&
						fleetIds?.[0] !== "all" &&
						fleetIds.length !== fleetIdsAll.length,
				];

				if (isTaxiServiceIds) {
					retval = retval.filter(
						(item) =>
							item.taxiServiceId &&
							taxiServiceIds &&
							taxiServiceIds.includes(item.taxiServiceId),
					);
				}

				if (isFleetIds) {
					// isUserFleetOwner
					retval = retval.filter((item) =>
						((fleetIds as number[]) || []).includes(
							item.park?.id ?? -2,
						),
					);
				}

				if (isOrderIds) {
					retval = retval.filter(
						(item) =>
							orderIds &&
							item.order?.id &&
							orderIds.includes(item.order?.id),
					);
				}

				if (isExecutorWorkingStatus) {
					retval = retval.filter(
						(item) =>
							item.executor?.isWorking === executorWorkingStatus,
					);
				}

				if (isExecutorStatuses) {
					retval = retval.filter(
						(item) =>
							executorStatuses &&
							item.executor?.status &&
							executorStatuses.includes(item.executor?.status),
					);
				}

				if (isLimit) {
					retval = retval.slice(0, limit);
				}

				return retval;
			},
			[executorLocationsSubscribe, fleetIdsAll],
		);

		return useMemo(
			() => ({
				getAllExecutorLocationsSubHandler,
			}),
			[getAllExecutorLocationsSubHandler],
		);
	};

declare namespace useExecutorLocationsSubHandler {
	interface Props {
		getAllExecutorLocationsSubHandler: (
			data: SmallSubscribeOptions,
		) => ExecutorLocations.Model[];
	}

	interface SmallSubscribeOptions {
		taxiServiceIds?: number[];
		limit?: number;
		//
		orderIds?: number[];
		executorWorkingStatus?: boolean;
		executorStatuses?: ExecutorStatus[];
		fleetIds?: number[] | ["all"];
	}
}

export default useExecutorLocationsSubHandler;
